/**
 * MyDealerForm Component
 * Main orchestrator for My Dealer feature using feature-based architecture
 * Maintains original UI design exactly as MyDealers.js
 */

import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import { RiDirectionFill } from "react-icons/ri";
import { MdLocationOff } from "react-icons/md";
import _ from "lodash";
import { SearchableDropdown } from "@features/shared";
import { useNotification } from "@shared/hooks";
import { getUniqueListBy } from "@components/Common/Function";
import Loader from "@features/shared/components/Loader";
import Message from "@features/shared/components/Message";
import {
  GoogleMap,
  VisitDetails,
  MyDiary,
  MySR,
  MyDiaryList,
} from "@shared/components";
import PowerBi from "@pages/PowerBi";
import MyDealerSummary from "./MyDealerSummary";
import MyDealerStatus from "./MyDealerStatus";
import DealerAuditPopup from "./DealerAuditPopup";
import styles from "@components/MarketWorking/MyTeam/MyDealer.module.css";
import { getMyDiaryDealerDetails } from "@redux/actions/mydiaryDetails-actions";
import { useMyDealerState } from "../hooks/useMyDealerState";
import { useLocation } from "../hooks/useLocation";
import { useCheckIn } from "../hooks/useCheckIn";
import { useDealerData } from "../hooks/useDealerData";
import { useLcdSurvey } from "../hooks/useLcdSurvey";
import LcdSurveyModal from "./LcdSurveyModal";
import { myDealerService } from "../services/myDealerService";
import { LOADING_STATES, DEALER_PLACEHOLDER } from "../hooks/constants";

const MyDealerForm = () => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();

  // Initialize hooks
  const state = useMyDealerState();
  const location = useLocation();
  const lcdSurvey = useLcdSurvey({ showSuccess, showError });
  const checkIn = useCheckIn({
    distanceToDealer: location.distanceToDealer,
    currentAccuracy: location.currentAccuracy,
    accuracy: state.accuracy,
    dealer: state.dealer,
    dealerCode: state.dealerCode,
    territoryCheckIn: state.territoryCheckIn,
    tsiCode: state.selectedTerritory?.SH2_Code__c,
    currentLatitude: location.currentLatitude,
    currentLongitude: location.currentLongitude,
    dealerLatitude: location.dealerLatitude,
    dealerLongitude: location.dealerLongitude,
    userType: state.userType,
    userEmail: state.userEmail,
    showSuccess,
    showError,
    setLoading: state.setLoading,
    setCheckinFlag: state.setCheckinFlag,
    setReloadDealerStatusFlag: state.setReloadDealerStatusFlag,
    setAutoCheckInFlag: state.setAutoCheckInFlag,
    setIsDealerPopupOpen: state.setIsDealerPopupOpen,
    setRecordId: state.setRecordId,
    autoCheckInFlag: state.autoCheckInFlag,
    checkLcdEligibility: lcdSurvey.checkEligibility,
    openLcdSurvey: lcdSurvey.openSurvey,
    clusterCode: state.cluster?.CLUSTER_CODE__c || state.cluster?.SH3_Code__c,
    clusterName: state.cluster?.CLUSTER_NAME__c || state.cluster?.SH3_Name__c,
  });

  const dealerData = useDealerData({
    selectedTerritory: state.selectedTerritory,
    sh3CodeForTerritory: state.sh3CodeForTerritory,
    tourPlanDefaultData: state.tourPlanDefaultData,
    clusterCodeOption: state.clusterCodeOption,
    territoryOptions: state.territoryOptions,
    userType: state.userType,
    isChecked: state.isChecked,
    setDealerOption: state.setDealerOption,
    setIsDealerData: state.setIsDealerData,
    showError,
    setTerritoryPickList: state.setTerritoryPickList,
    setCluster: state.setCluster,
    setSH3CodeForTerritory: state.setSH3CodeForTerritory,
    setSelectedTerritory: state.setSelectedTerritory,
    setLoading: state.setLoading,
  });

  // Handle dealer selection
  useEffect(() => {
    if (state.selectLatLong && state.selectLatLong.Id) {
      // Set dealer location with coordinates
      const lat = state.selectLatLong?.DealerLatitude__c;
      const lng = state.selectLatLong?.DealerLongitude__c;

      if (lat != null && lng != null) {
        location.setDealerLocation(lat, lng);
      }

      state.setDealer(state.selectLatLong?.Dealer_Name__c);
      state.setDealerCode(state.selectLatLong?.DealerCode__c);
      state.setAccuracy(parseInt(state.selectLatLong?.GPSAccuracy__c));

      // Dispatch to Redux for MyDiary integration
      dispatch(getMyDiaryDealerDetails(state.selectLatLong?.DealerCode__c));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectLatLong?.Id, dispatch]);

  // Update territory check-in when territory changes
  useEffect(() => {
    if (state.selectedTerritory?.Id) {
      state.setTerritoryCheckIn(state.selectedTerritory.Id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedTerritory?.Id]);

  // Handle check-in
  const handleCheckIn = useCallback(() => {
    checkIn.handleCheckInClick();
  }, [checkIn]);

  // Auto-populate dealer form fields from the summary card and trigger check-in
  // Mirrors handleAutoPoulatedValue from the legacy MyDealers.js
  const handleAutoPoulatedValue = useCallback(
    async (value) => {
      // Find territory
      const tty = (state.territoryOptions || []).find(
        (t) => t.SH2_Code__c === value.tsicode
      );
      if (!tty) {
        showError("Territory Not Found");
        return;
      }

      state.setSelectedTerritory({
        attributes: tty.attributes,
        Id: tty.Id,
        SH2_Name__c: tty.SH2_Name__c,
        SH2_Code__c: value.tsicode,
        Name: null,
        SH3_Code__c: tty.SH3_Code__c,
      });

      // Fetch dealer list for this territory
      const response = await myDealerService.getDealers({
        code: value.tsicode,
        isjointlyadopteddealer: state.isChecked,
      });

      if (response.success && response.data.length > 0) {
        state.setDealerOption(response.data);
        const dealerEntry = response.data.find(
          (d) => d.DealerCode__c === value.dealercode
        );
        if (dealerEntry) {
          state.setSelectLatLong({
            ...dealerEntry,
            Dealer_Name__c: value.dealername,
            DealerLatitude__c: value.dealerlatitude,
            DealerLongitude__c: value.dealerlongitude,
          });
        } else {
          showError("Dealer Not Found");
          return;
        }
      } else {
        showError("Dealer Not Found");
        return;
      }

      // Signal auto check-in — the effect below will fire once dealer/territory state settles
      state.setAutoCheckInFlag(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, showError]
  );

  // Auto check-in when all required fields are populated via handleAutoPoulatedValue
  useEffect(() => {
    if (
      state.territoryCheckIn &&
      state.dealer &&
      state.selectLatLong?.Id &&
      state.autoCheckInFlag === true
    ) {
      checkIn.performCheckIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.territoryCheckIn, state.dealer, state.autoCheckInFlag]);

  // Handle navigate button
  const showInMapClicked = useCallback(() => {
    location.openDealerInMaps();
  }, [location]);

  // Handle switch change
  const handleSwitchChange = useCallback(
    (event) => {
      state.setIsChecked(event.target.checked);
    },
    [state.setIsChecked],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize territory options
  const territoryOptions = useMemo(
    () =>
      _.isEmpty(state.territoryOptions)
        ? []
        : getUniqueListBy(state.territoryOptions, "SH2_Code__c"),
    [state.territoryOptions],
  );

  // Memoize dealer options based on loading state
  const dealerOptions = useMemo(() => {
    if (state.isDealerData === LOADING_STATES.LOADING) {
      return [{ Dealer_Name__c: "Loading...", DealerCode__c: "" }];
    }
    if (state.isDealerData === LOADING_STATES.NO_DATA) {
      return [{ Dealer_Name__c: "No Data", DealerCode__c: "" }];
    }
    return state.dealerOption;
  }, [state.isDealerData, state.dealerOption]);

  // Handle territory change
  const handleTerritoryChange = useCallback(
    (event, newValue) => {
      state.setSelectedTerritory(newValue);
      state.setSelectLatLong(DEALER_PLACEHOLDER);
      state.setDealerOption([]);
    },
    [state.setSelectedTerritory, state.setSelectLatLong, state.setDealerOption],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle dealer change
  const handleDealerChange = useCallback(
    (event, newValue) => {
      state.setSelectLatLong(newValue);
    },
    [state.setSelectLatLong],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Format territory label
  const getTerritoryLabel = useCallback((option) => {
    return option?.SH2_Name__c
      ? `${option.SH2_Name__c} : ${option.SH2_Code__c}`
      : "";
  }, []);

  // Format dealer label
  const getDealerLabel = useCallback((option) => {
    return option?.Dealer_Name__c
      ? `${option.Dealer_Name__c} ${option.DealerCode__c ? ":" : ""} ${option.DealerCode__c || ""}`
      : "";
  }, []);

  return (
    <>
      <Loader loading={state.loading} />

      {state.userType === "SH3" && (
        <>
          {/* My dealer Summary Module*/}
          <MyDealerSummary
            CheckIndata={handleAutoPoulatedValue}
            getCheckInStatus={state.checkinFlag}
            reloadList={state.getSummeryList}
            isSubmittedFlag={() => {
              state.setReloadDealerStatusFlag(true);
              state.setCheckinFlag(false); // Reset so next approval can re-trigger the effect
            }}
          />

          {/* My dealer Status Module*/}
          <MyDealerStatus
            reloadList={state.reloadDealerStatusFlag}
            afterReloadFlag={() => state.setReloadDealerStatusFlag(false)}
          />
        </>
      )}

      <div className={styles.myDealer__outerBox}>
        <div className="row">
          <div className={`col-4 ${styles.myDealer__heading}`}>My Dealer</div>
          <div className={`col-8 ${styles.myDealer__switchOuterBox}`}>
            {state.salesGroupCode &&
              state.userType &&
              (state.salesGroupCode === 105 || state.salesGroupCode === 107) &&
              ["SH3", "SH4", "SH5", "SH6", "SH7"].includes(state.userType) && (
                <>
                  <label className={styles.myDealer__switchButton}>
                    Jointly Adopted Dealer
                  </label>
                  <div
                    className={`form-check form-switch ${styles.myDealer__switchBox}`}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={state.isChecked}
                      onChange={handleSwitchChange}
                    />
                  </div>
                </>
              )}
          </div>
        </div>

        <div className="row w-full">
          <SearchableDropdown
            options={territoryOptions}
            optionKey="SH2_Name__c"
            value={state.selectedTerritory}
            onChange={handleTerritoryChange}
            getOptionLabel={getTerritoryLabel}
            placeholder="Select Territory"
            disableCheckbox={true}
          />

          <SearchableDropdown
            options={dealerOptions}
            optionKey="Dealer_Name__c"
            value={state.selectLatLong}
            onChange={handleDealerChange}
            getOptionLabel={getDealerLabel}
            placeholder="Select Dealer Name"
            disableCheckbox={true}
          />
        </div>

        <div className={styles.myDealer__googleMap_Box}>
          {location.locationError ? (
            <Message
              message={
                "Location access denied. Please update your browser settings to grant location permission."
              }
              className="DealerLocatorForm__mapAccess"
              icon={
                <MdLocationOff
                  style={{ color: "#808080", height: "2em", width: "2em" }}
                />
              }
            />
          ) : (
            <GoogleMap
              lat={
                location.dealerLatitude != null
                  ? parseFloat(location.dealerLatitude)
                  : location.currentLatitude
              }
              long={
                location.dealerLongitude != null
                  ? parseFloat(location.dealerLongitude)
                  : location.currentLongitude
              }
            />
          )}
        </div>

        <div className="row m-0 p-2">
          <div className="col-4 ps-0">
            <Link to="#">
              <Button
                className={styles.myDealer__navButton}
                onClick={showInMapClicked}
              >
                Navigate
                <RiDirectionFill
                  style={{ marginLeft: "4px", marginBottom: "1px" }}
                />
              </Button>
            </Link>
          </div>
          <div className="col-4">
            <Button
              className={styles.myDealer__navButton}
              onClick={handleCheckIn}
            >
              Check In
            </Button>
          </div>
          <div className="col-4 pe-0">
            <Link to="#">
              <Button
                className={styles.myDealer__navButton}
                onClick={() => {
                  if (!_.isEmpty(state.dealerCode)) {
                    state.setIsPreviousObservationOpen(true);
                  } else {
                    showError(
                      "Please select dealer to view previous observations",
                    );
                  }
                }}
              >
                Previous Observations
              </Button>
            </Link>
          </div>
        </div>

        <div className="row m-0 p-2 flex justify-content-center align-items-center">
          <div className="col-4 ps-0">
            <Link to="#">
              <Button
                className={styles.myDealer__navButton}
                onClick={() => {
                  if (!_.isEmpty(state.dealerCode)) {
                    state.setIsMyDiaryOpen(true);
                  } else {
                    showError(
                      "Please select dealer to view and submit my diary",
                    );
                  }
                }}
              >
                My Diary
              </Button>
            </Link>
          </div>

          <div className="col-4">
            <Link to="#">
              <Button
                className={styles.myDealer__navButton}
                onClick={() => {
                  if (!_.isEmpty(state.dealerCode)) {
                    state.setIsMySROpen(true);
                  } else {
                    showError("Please select dealer to view my SR");
                  }
                }}
              >
                My SR
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Power BI Table */}
      <div className="pbi__outerBox">
        <PowerBi
          id="7d086498-f679-4a6b-b1bf-892ad94f12f7"
          pageName={{
            id: "db748da0b2074365a019",
            name: "My Dealer",
          }}
          dealerCode={state.dealerCode}
        />
      </div>
      <div className="pbi__outerBox">
        <PowerBi
          id="fe64310f-4e8e-428e-8e37-dbcc99589108"
          pageName={{
            id: "f5339b70d5400b4d66d9",
            name: "PCC Overview Dashboard",
          }}
          dealerCode={state.dealerCode}
          filterName="DimHierarchy"
        />
      </div>

      <DealerAuditPopup
        selectLatLong={state.selectLatLong}
        isOpen={state.isDealerPopupOpen}
        setIsOpen={state.setIsDealerPopupOpen}
        onSubmit={(data) => {
          checkIn.proceedHOAuditorStampIn(data, state.recordId);
        }}
      />

      <LcdSurveyModal
        isOpen={lcdSurvey.isOpen}
        submitting={lcdSurvey.submitting}
        context={lcdSurvey.context}
        onSubmit={lcdSurvey.submitSurvey}
      />

      {state.isPreviousObservationOpen && (
        <MyDiaryList
          isOpen={state.isPreviousObservationOpen}
          setIsOpen={state.setIsPreviousObservationOpen}
        />
      )}
      {state.isMyDiaryOpen && (
        <MyDiary
          selectedDealerCode={state.dealerCode}
          selectedDealerWSS={state.dealer}
          isOpen={state.isMyDiaryOpen}
          setIsOpen={state.setIsMyDiaryOpen}
        />
      )}

      {state.isMySROpen && (
        <MySR
          name="Dealer"
          isOpen={state.isMySROpen}
          setIsOpen={state.setIsMySROpen}
          selectedDealerCode={state.dealerCode}
          selectedDealerWSS={state.dealer}
        />
      )}

      <VisitDetails />
    </>
  );
};

export default React.memo(MyDealerForm);
